import employeeCalendarEventApi from "apis/employeeCalendarEvents";
import { WebApiRequest, HTTP_POST, HTTP_PUT, HTTP_DELETE, HTTP_GET } from "tools/webservice";
import { CalendarEntry, CalendarTimeType } from "mueller-controls";
import moment from "moment";

// copies an object and fixes all of its moment times to serialize in their regular time for the server (essentially removes the timezone)
// only copies one deep
const fixMomentTimesForServer = object => {
  let copied = { ...object };

  Object.keys(copied).forEach(k => {
    if (moment.isMoment(copied[k])) {
      // grab the utc offset from the date itself and not moment because you could have a timezone of -5 but create a date with a -4 timezone because you created a date in the future after DST....
      copied[k] = copied[k].clone().add(copied[k].utcOffset(), "minutes");
    }
  });

  return copied;
};

export const addEmployeeCalendarEvent = (event, successCallback, errorCallback, mountedRef) => {
  let postEvent = { ...event, timezoneOffsetHours: new Date().getTimezoneOffset() / 60 };
  WebApiRequest({
    method: HTTP_POST,
    payload: fixMomentTimesForServer(postEvent),
    endpoint: employeeCalendarEventApi.addCalendarEvent(),
    successCallback,
    errorCallback,
    mountedRef,
  });
};

// copy everything from the server data structure because they are the same, but then fix some of the values that dont just magically deserialize properly
const mapReturnToCalendarEntry = e => new CalendarEntry({
  ...e,
  timeType: Object.values(CalendarTimeType).find(v => v.id === e.calendarEventType),
  startDate: moment(e.startDateTime),
  endDate: moment(e.endDateTime),
  recursUntilDate: e.recursUntilDate && moment(e.recursUntilDate),
});

export const getCalendarEventsForSignedInUser = (startDateTime, endDateTime, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
      method: HTTP_POST,
      payload: {
        startDateTime: startDateTime,
        endDateTime: endDateTime,
      },
      endpoint: employeeCalendarEventApi.getCalendarEvent(),
      successCallback: events => successCallback(events.map(mapReturnToCalendarEntry)),
      errorCallback,
      mountedRef,
    });

export const getCalendarEventsByEmployeeId = (employeeId, startDateTime, endDateTime, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
      method: HTTP_POST,
      payload: {
        startDateTime: startDateTime,
        endDateTime: endDateTime,
      },
      endpoint: employeeCalendarEventApi.getCalendarEventByEmployeeId(employeeId),
      successCallback: events => successCallback(events.map(mapReturnToCalendarEntry)),
      errorCallback,
      mountedRef,
    });

export const updateEmployeeCalendarEvent = (event, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
    method: HTTP_PUT,
    payload: fixMomentTimesForServer(event),
    endpoint: employeeCalendarEventApi.updateCalendarEvent(event.id),
    successCallback,
    errorCallback,
    mountedRef,
  });

export const deleteEmployeeCalendarEvent = (payload, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
    method: HTTP_DELETE,
    payload: payload,
    endpoint: employeeCalendarEventApi.deleteCalendarEvent(payload.id),
    successCallback,
    errorCallback,
    mountedRef,
  });

export const getComplianceForSignedInUser = (successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
    method: HTTP_GET,
    endpoint: employeeCalendarEventApi.getCompliance(),
    successCallback: compliance => successCallback(compliance),
    errorCallback,
    mountedRef,
  });

export const getComplianceByEmployeeId = (employeeId, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
    method: HTTP_GET,
    endpoint: employeeCalendarEventApi.getComplianceByEmployeeId(employeeId),
    successCallback: compliance => successCallback(compliance),
    errorCallback,
    mountedRef,
  });

/**
 * @typedef {import("interfaces/Mueller.Business.Models").EmployeeAvailableListResponse} EmployeeAvailableListResponse
 * @typedef {import("interfaces/Mueller.Business.Models").EmployeeAvailableListRequest} EmployeeAvailableListRequest
 * @param {number} supervisorId
 * @param {EmployeeAvailableListRequest} request
 * @param {(data: EmployeeAvailableListResponse) => void} successCallback
 * @param {(error: any) => void} errorCallback
 * @param {import("react").RefObject<boolean>} mountedRef
 * @returns {Promise<EmployeeAvailableListResponse>}
 */
export const getEmployeeAvailableList = (supervisorId, request, successCallback, errorCallback, mountedRef) =>
  WebApiRequest({
    method: HTTP_POST,
    payload: request,
    endpoint: employeeCalendarEventApi.getEmployeeAvailableList(supervisorId),
    successCallback: compliance => successCallback(compliance),
    errorCallback,
    mountedRef,
  });
